import { Button, Dialog, Grid, LinearProgress, Stack, Typography } from "@mui/material";
import { styled } from "@mui/system";
import React from "react";
import { useHistory } from "react-router-dom";
import ContainedButton from "src/components/common/ContainedButton";
import ReferLegalDialog from "src/components/legal/ReferLegalDialog";
import useDeviceSizes from "src/components/portfolio/hooks/useDeviceSizes";
import { RafTiers, setUserRaf, useUserSlice } from "src/store/features/user.slice";
import ProfileBanner from "../Profile/ProfileBanner";
import RaFProgressBar from "../Profile/RaFProgressBar";
import QRCodeBadge from "../extra/QRCodeBadge";
import { useDialogStyles } from "src/components/UI/AreYouTalkingWithABrokerDialog";
import api from "src/store/features/api";
import { useAppDispatch } from "src/store/store";
import { trackEvent } from "src/Amplitude";
import useVerificationNavigation from "src/components/common/useVerificationNavigation";

const Wrapper = styled("div")(() => ({
  display: "flex",
  padding: "24px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-end",
  gap: "24px",
  alignSelf: "stretch",
  borderRadius: "16px",
  background: "#FFF",
  boxShadow: "0px 4px 20px 0px rgba(173, 192, 203, 0.30)",
}));

const TopWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: "16px",
  alignSelf: "stretch",
}));

const ButtonWrapper = styled("div")(() => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",
  top: 0,
  right: 0,
}));

interface TooltipProps {
  show: boolean;
}
const Tooltip = styled("div")<TooltipProps>(({ show }) => ({
  padding: "12px 16px",
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  borderRadius: "10px",
  background: "rgba(0, 32, 53, 0.80)",
  color: "#FFF",
  fontFamily: "Söhne",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "14px",
  position: "absolute",
  bottom: 80,
  zIndex: 1,

  // smooth display transition
  transition: "opacity 0.2s linear, visibility 1s ease",
  opacity: show ? 1 : 0,
  visibility: show ? "visible" : "hidden",
}));

interface TierProgessBannerProps {
  name: string;
  customerID: string;
}

const TierProgessBanner = (props: TierProgessBannerProps) => {
  const { name, customerID } = props;
  const { isMobile } = useDeviceSizes();
  const dispatch = useAppDispatch();
  const { handleVerification } = useVerificationNavigation();

  const {
    raf: { tier, rafCode, rafOptedIn, OLD_RAF_SYSTEM },
    isUnverified,
    isPendingVerification,
  } = useUserSlice();

  const [clicked, setClicked] = React.useState<boolean>(false);
  const handleSharecode = () => {
    // end function if false
    if (!window.isSecureContext) {
      return;
    }

    navigator.clipboard.writeText(`app.calebandbrown.com/signup?referrer=${rafCode}`);
    setClicked(true);
    trackEvent("Clicked share code button", { page: "RaF" }, "RaF");
    setTimeout(() => {
      setClicked(false);
    }, 1000);
  };

  const handleCreateCode = () => {
    setOpenReferLegal(true);
  };

  const [openReferLegal, setOpenReferLegal] = React.useState<boolean>(false);
  const [fetchUrlError, setFetchUrlError] = React.useState<boolean>(false);
  const handleErrorModelClose = () => setFetchUrlError(false);
  const [skip, setSkip] = React.useState<boolean>(true);
  const { data: optIn, isLoading, isFetching } = api.useCreateReferralUrlQuery(undefined, { skip });
  const handleOptIn = () => {
    setSkip(false);
  };
  const handleCloseReferLegal = () => {
    setOpenReferLegal(false);
  };

  React.useEffect(() => {
    if (optIn) {
      dispatch(
        setUserRaf({
          tier: optIn.tier as RafTiers,
          referredAmount: optIn.referees,
          rafCode: optIn.referralCode,
          rafOptedIn: optIn.referralOptIn,
          OLD_RAF_SYSTEM: !["bronze", "silver", "gold", "black"].includes(optIn.tier),
        }),
      );
      trackEvent("Clicked accept to opt in", { page: "RaF" }, "RaF");
    }
  }, [optIn]);

  return (
    <Wrapper>
      <TopWrapper>
        <ProfileBanner header={name} customerID={customerID} tier={tier} />
        {!OLD_RAF_SYSTEM && <RaFProgressBar tier={tier} />}
        {isMobile && (
          <>
            {!isUnverified && (
              <div style={{ height: "295px" }}>
                <QRCodeBadge tier={tier} backgroundSize="100%" qrSize={236} />
              </div>
            )}
          </>
        )}
      </TopWrapper>

      <ButtonWrapper id="RaF-buttonWrapper">
        {isUnverified || isPendingVerification ? (
          <ContainedButton color="salmon" width="100%" height="46px" onClick={handleVerification}>
            Verify account
          </ContainedButton>
        ) : !rafOptedIn ? (
          <>
            <ContainedButton color="navy" width="100%" height="46px" onClick={handleCreateCode}>
              Click here to start earning
            </ContainedButton>
          </>
        ) : (
          <>
            <Tooltip show={clicked}>Code copied to clipboard!</Tooltip>
            <ContainedButton
              id="RaF-shareInviteButton"
              color="navy"
              width="100%"
              height="46px"
              onClick={handleSharecode}>
              Share invite
            </ContainedButton>
          </>
        )}
        {(isLoading || isFetching) && (
          <div style={{ width: "100%", padding: "4px 0px" }}>
            <LinearProgress color="primary" />
          </div>
        )}
      </ButtonWrapper>

      <ReferLegalDialog
        refetchUserQuery={handleOptIn}
        optIn={openReferLegal}
        setOptIn={setOpenReferLegal}
        setFetchUrlError={setFetchUrlError}
        onClose={handleCloseReferLegal}
      />
      <ErrorDialog open={fetchUrlError} onClose={handleErrorModelClose} />
    </Wrapper>
  );
};

export default TierProgessBanner;

interface ErrorDialogProps {
  open: boolean;
  onClose: () => void;
}

const ErrorDialog = (props: ErrorDialogProps) => {
  const { open, onClose } = props;
  const classes = useDialogStyles();
  const errorMsg = "Failed to create referral account. Please contact support";

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.paperProps }}>
      <Stack spacing={2}>
        <Grid item container justifyContent="center">
          <Typography align="center" sx={{ fontSize: "14px" }}>
            {errorMsg}
          </Typography>
        </Grid>
        <Grid container justifyContent="center" alignItems="center">
          <Button disableRipple onClick={onClose} className={classes.submitButton}>
            Close
          </Button>
        </Grid>
      </Stack>
    </Dialog>
  );
};
